import { useEffect, useState } from "react";
import { Loader2, Layers, Ruler, Info, MessageCircle } from "lucide-react";
import { analyzeMaterials } from "@/lib/api";
import { trackEvent, trackClick } from "@/lib/analytics";

const CAT_COLORS = {
  Lantai: "from-amber-400 to-orange-500",
  Dinding: "from-sky-400 to-cyan-500",
  Plafon: "from-slate-400 to-slate-600",
  Furnitur: "from-emerald-400 to-green-600",
  Pencahayaan: "from-yellow-400 to-amber-500",
  Aksesoris: "from-fuchsia-400 to-pink-500",
  Sanitary: "from-cyan-400 to-teal-500",
  Dekorasi: "from-fuchsia-400 to-pink-500",
  Umum: "from-slate-400 to-slate-500",
};

export default function MaterialsPanel({ style, roomType, luasRuangan, imageBase64 }) {
  const [loading, setLoading] = useState(true);
  const [materials, setMaterials] = useState([]);
  const [disclaimer, setDisclaimer] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    trackEvent("materials_request", { style, room_type: roomType, luas: luasRuangan || "" });
    analyzeMaterials({ style, room_type: roomType, luas_ruangan: luasRuangan, image_base64: imageBase64 })
      .then((res) => {
        if (cancelled) return;
        setMaterials(res.materials || []);
        setDisclaimer(res.disclaimer || "");
        trackEvent("materials_success", { count: (res.materials || []).length });
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Gagal memuat rekomendasi material.");
        trackEvent("materials_error", { status: e?.response?.status || 0 });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [style, roomType, luasRuangan, imageBase64]);

  const gotoConsult = () => {
    trackClick("materials_goto_consultation");
    document.getElementById("konsultasi")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="mt-10 bg-white border border-sky-100 rounded-3xl p-6 lg:p-8 shadow-lg shadow-sky-100/40" data-testid="materials-panel">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
          <Layers size={22} className="text-white" strokeWidth={1.5} />
        </div>
        <div>
          <h4 className="font-display text-2xl text-slate-900">Material pada Hasil Desain Anda</h4>
          <p className="text-sm text-slate-600 mt-1">
            AI menganalisis foto hasil desain — berikut nama material yang terlihat beserta manfaat, deskripsi, dan perkiraan warna/finishing-nya.
          </p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12 text-slate-500" data-testid="materials-loading">
          <Loader2 size={20} className="animate-spin mr-3" /> Menganalisis material yang terlihat pada desain Anda…
        </div>
      )}

      {error && !loading && (
        <div className="py-8 text-center text-sm text-red-600" data-testid="materials-error">{error}</div>
      )}

      {!loading && !error && materials.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="materials-list">
          {materials.map((m, i) => (
            <div key={`${m.name}-${i}`} data-testid={`material-item-${i}`} className="border border-slate-100 rounded-2xl p-5 hover:border-sky-200 hover:shadow-md transition-all">
              <div className="flex items-center justify-between mb-3">
                <span className={`inline-block px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-semibold text-white bg-gradient-to-r ${CAT_COLORS[m.category] || CAT_COLORS.Umum}`}>
                  {m.category || "Umum"}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">#{i + 1}</span>
              </div>
              <div className="font-display text-lg text-slate-900 mb-1" data-testid={`material-name-${i}`}>{m.name}</div>
              {m.finish && (
                <div className="inline-block text-[11px] text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-3 py-1 mb-3" data-testid={`material-finish-${i}`}>
                  {m.finish}
                </div>
              )}
              <p className="text-sm text-slate-700 leading-relaxed mb-2" data-testid={`material-benefit-${i}`}>
                <span className="font-semibold text-slate-900">Manfaat: </span>{m.benefit}
              </p>
              {m.description && (
                <p className="text-sm text-slate-500 leading-relaxed" data-testid={`material-description-${i}`}>{m.description}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Info banner: ukuran didapat setelah submit form */}
      <div className="mt-6 bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 rounded-2xl p-5 lg:p-6" data-testid="materials-cta">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-sky-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5">
            <Ruler size={16} />
          </div>
          <div>
            <div className="font-display text-lg text-slate-900 mb-1">Ingin tahu ukuran material yang tepat?</div>
            <p className="text-sm text-slate-700 leading-relaxed">
              {disclaimer || "Ukuran & kuantitas material yang presisi akan dikirim tim desainer kami setelah Anda menyelesaikan konsultasi."}
              <br />
              Lengkapi form di bawah dengan <b>nama lengkap, email, no. WhatsApp, luas ruangan, jenis ruangan,
              budget, preferensi gaya, dan catatan tambahan</b> → tekan <b>"Kirim Permintaan Konsultasi"</b>.
              Konsultan interior kami akan langsung menghubungi Anda untuk membahas ukuran material lebih detail.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={gotoConsult}
            data-testid="materials-consultation-cta"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white font-semibold text-xs uppercase tracking-[0.2em] px-6 py-3 rounded-full shadow-md shadow-sky-200 transition-colors"
          >
            <MessageCircle size={14} /> Isi Konsultasi Sekarang
          </button>
          <span className="inline-flex items-center gap-2 text-xs text-slate-500">
            <Info size={12} /> Gratis · konsultan bersertifikasi · respon &lt; 1×24 jam
          </span>
        </div>
      </div>
    </div>
  );
}
